import {
    Button,
    Card,
    Separator,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@repo/shadcn';
import { Loader2Icon } from 'lucide-react';
import { useFormContext } from 'react-hook-form';

import { TextAreaField } from '@components/form/fields';

import { formatCurrencyInBDT } from '@/utils';

import { useCreatePurchaseReturnContext } from './create-purchase-return.context';
import { TCreatePurchaseReturnForm } from './create-purchase-return.schema';

export const PurchaseOverview = () => {
    const {
        isPurchaseDetailsFetching,
        isPurchaseDetailsError,
        isPurchaseDetailsLoading,
        data: _,
        isSubmitting,
    } = useCreatePurchaseReturnContext();

    const { watch, formState } = useFormContext<TCreatePurchaseReturnForm>();

    const { isValid } = formState;

    const items = watch('items');

    const selectedItems = items?.filter((item) => item.is_selected) || [];

    const totalAmount = selectedItems.reduce((acc, item) => {
        return acc + item.price * item.return_quantity;
    }, 0);

    const isSubmitDisabled =
        !isValid || selectedItems.length === 0 || totalAmount <= 0;

    if (isPurchaseDetailsFetching || isPurchaseDetailsLoading) {
        return (
            <Card
                className={
                    'flex flex-col items-center justify-center gap-2 px-4 py-8'
                }
            >
                <Loader2Icon className={'animate-spin'} />
            </Card>
        );
    }

    if (isPurchaseDetailsError) {
        return (
            <Card className={'p-4 text-red-600'}>
                Error loading purchase details.
            </Card>
        );
    }

    const renderItems = () => {
        if (!selectedItems || selectedItems.length === 0) {
            return (
                <p className={'p-4 text-center'}>
                    Check items to add for return.
                </p>
            );
        }

        return (
            <Table className={'px-0'}>
                <TableHeader>
                    <TableRow>
                        <TableHead className={'pl-0'}>Name</TableHead>
                        <TableHead className={'text-right'}>Price</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                        <TableHead className="pr-0 text-right">Total</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody className={'px-0'}>
                    {selectedItems.map((item) => (
                        <TableRow key={item.purchase_item_id}>
                            <TableCell className={'pl-0'}>
                                {item.purchase_item_name}
                            </TableCell>

                            <TableCell className={'text-right'}>
                                {formatCurrencyInBDT(item.price)}
                            </TableCell>

                            <TableCell className="text-right">
                                {item.return_quantity}
                            </TableCell>

                            <TableCell className={'pr-0 text-right'}>
                                {formatCurrencyInBDT(
                                    item.price * item.return_quantity,
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    };

    const renderFooter = () => {
        if (!selectedItems || selectedItems.length === 0) {
            return null;
        }
        return (
            <>
                <Separator />
                <div className={'flex items-end justify-end gap-4'}>
                    <strong>Total Amount: </strong>
                    <strong>{formatCurrencyInBDT(totalAmount)}</strong>
                </div>

                <div>
                    <label className={'mb-2 block font-medium'}>Reason</label>
                    <TextAreaField<TCreatePurchaseReturnForm>
                        name={'reason'}
                        placeholder={'Enter return reason'}
                    />
                </div>

                <div className={'flex justify-end'}>
                    <Button
                        type={'submit'}
                        disabled={isSubmitDisabled || isSubmitting}
                    >
                        {isSubmitting ? (
                            <Loader2Icon
                                className={'mr-2 h-4 w-4 animate-spin'}
                            />
                        ) : (
                            'Submit'
                        )}
                    </Button>
                </div>
            </>
        );
    };

    return (
        <Card className={'space-y-4 p-4'}>
            <h3 className={'text-lg font-medium'}>Return Overview</h3>

            {renderItems()}
            {renderFooter()}
        </Card>
    );
};

PurchaseOverview.displayName = 'PurchaseOverview';
